import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

interface WorkerLive {
  online: boolean;
  activeWorkers: number;
  busyWorkers: number;
  throttled: boolean;
}

interface WorkerInfo {
  id: string;
  name: string;
  enabled: boolean;
  live?: WorkerLive;
}

const WorkerStatusBanner: React.FC = () => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState<WorkerInfo[]>([]);
  const [hasIssues, setHasIssues] = useState(false);
  const [issueText, setIssueText] = useState('');

  const checkWorkers = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/workers');
      if (!res.data?.success) return;

      const list: WorkerInfo[] = res.data.data || [];
      setWorkers(list);

      const enabled = list.filter(w => w.enabled);
      const offline = enabled.filter(w => !w.live?.online);
      const throttled = enabled.filter(w => w.live?.online && w.live?.throttled);
      const totalWorkers = enabled.reduce((sum, w) => sum + (w.live?.activeWorkers || 0), 0);

      const issues: string[] = [];

      if (enabled.length === 0) {
        issues.push('No workers configured');
      } else if (offline.length === enabled.length) {
        issues.push('All workers offline');
      } else if (offline.length > 0) {
        issues.push(`${offline.length} worker${offline.length > 1 ? 's' : ''} offline: ${offline.map(w => w.name).join(', ')}`);
      }

      if (throttled.length > 0) {
        issues.push(`${throttled.length} worker${throttled.length > 1 ? 's' : ''} throttled`);
      }

      if (totalWorkers === 0 && enabled.length > 0 && offline.length < enabled.length) {
        issues.push('All workers paused (0 active threads)');
      }

      setHasIssues(issues.length > 0);
      setIssueText(issues.join(' \u2022 '));
    } catch {
      // Silently fail — user may not be admin or API unavailable.
    }
  }, []);

  useEffect(() => {
    checkWorkers();
    const interval = setInterval(checkWorkers, 15000);
    return () => clearInterval(interval);
  }, [checkWorkers]);

  // Only show when there are issues — hide when everything is OK.
  if (!hasIssues) return null;

  const enabled = workers.filter(w => w.enabled);
  const online = enabled.filter(w => w.live?.online);

  return (
    <Tooltip title={<>{issueText}<br />Click to open worker settings</>}>
      {/* A Tag is not focusable by itself; role + tabIndex + Enter make the
          only alert in the header reachable from the keyboard as well. */}
      <Tag
        icon={<WarningOutlined />}
        color="error"
        role="button"
        tabIndex={0}
        aria-label={`Worker issues: ${issueText}. Open worker settings`}
        style={{ margin: 0, cursor: 'pointer' }}
        onClick={() => navigate('/settings')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/settings'); } }}
      >
        Workers {online.length}/{enabled.length}
      </Tag>
    </Tooltip>
  );
};

export default WorkerStatusBanner;
